/**
 * MODULE: _shared/frontend-shell/src/features/crm/hooks/useCrmAccountProducts.ts
 * PURPOSE: React Query hook for account-scoped product entitlements
 * PUBLIC_API: useCrmAccountProducts
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  crmCreateAccountProduct,
  crmDeleteAccountProduct,
  crmListAccountProducts,
  crmPatchAccountProduct,
  type CrmAccountProduct,
  type CrmProductAdoptionStatus,
} from '@shared/lib/frontend/api/crmAccountProductsApi';

export function useCrmAccountProducts(accountId: string | null | undefined, enabled = true) {
  const queryClient = useQueryClient();
  const queryKey = ['crm', 'account-products', accountId];

  const query = useQuery({
    queryKey,
    queryFn: () => crmListAccountProducts({ accountId: accountId!, limit: 50 }),
    enabled: Boolean(enabled && accountId),
    staleTime: 30_000,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey });

  const createMutation = useMutation({
    mutationFn: (body: {
      productId: string;
      status?: CrmProductAdoptionStatus;
      seats?: number;
      mrr?: number;
      notes?: string;
    }) => crmCreateAccountProduct({ accountId: accountId!, ...body }),
    onSuccess: invalidate,
  });

  const patchMutation = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Partial<CrmAccountProduct> }) =>
      crmPatchAccountProduct(id, patch),
    onSuccess: invalidate,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => crmDeleteAccountProduct(id),
    onSuccess: invalidate,
  });

  return {
    entitlements: query.data?.items ?? [],
    total: query.data?.total ?? 0,
    loading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
    createEntitlement: createMutation.mutateAsync,
    creating: createMutation.isPending,
    patchEntitlement: patchMutation.mutateAsync,
    patching: patchMutation.isPending,
    deleteEntitlement: deleteMutation.mutateAsync,
    deleting: deleteMutation.isPending,
  };
}
